import { PublicApiError, type PublicApiClient } from './client';
import { solveSpacePow } from '../security/pow';

export type AccountChallengePurpose =
  | 'register'
  | 'login'
  | 'recover_key'
  | 'recover_email_start'
  | 'recover_email_complete'
  | 'support_ticket'
  | 'text_claim'
  | 'sensitive_action';

export type AccountPowSolution = {
  powNonce: string;
  digest: string;
  iterations: number;
  solvedAt: number;
  elapsedMs: number;
};

export type AccountChallenge = {
  challengeId: string;
  purpose: string;
  powPrefix: string;
  difficulty: number;
  expiresAt?: string;
  ttlSeconds?: number;
};

export type AccountSecurityPayload = {
  challengeId: string;
  powNonce: string;
  honeypot: string;
  timing: {
    clientStartedAt: number;
    clientSubmittedAt: number;
    inputDurationMs?: number;
    focusDurationMs?: number;
  };
  pow: AccountPowSolution;
};

export type AccountMe = {
  ok: boolean;
  account?: {
    id?: string;
    handle?: string;
    pseudonym?: string;
    status?: string;
    publicProfileCreated?: false;
    publicAuthorExposed?: false;
  } | null;
  securityHold?: unknown;
};

export type AccountRuntimeItem = Record<string, unknown>;

export async function fetchAccountChallenge(
  api: PublicApiClient,
  purpose: AccountChallengePurpose,
): Promise<AccountChallenge> {
  const response = await api.request<{ ok: boolean; challenge: AccountChallenge }>(
    `/api/account/challenge?purpose=${encodeURIComponent(purpose)}`,
  );
  return response.challenge;
}

export async function prepareAccountSecurity(
  api: PublicApiClient,
  purpose: AccountChallengePurpose,
  clientStartedAt: number,
  options: {
    honeypot?: string;
    inputDurationMs?: number;
    focusDurationMs?: number;
    signal?: AbortSignal;
  } = {},
): Promise<AccountSecurityPayload> {
  const challenge = await fetchAccountChallenge(api, purpose);
  const pow = await solveSpacePow(
    {
      ok: true,
      challengeId: challenge.challengeId,
      powPrefix: challenge.powPrefix,
      difficulty: challenge.difficulty,
      expiresAt: challenge.expiresAt || '',
    } as never,
    { abortSignal: options.signal },
  );
  return {
    challengeId: challenge.challengeId,
    powNonce: pow.powNonce,
    honeypot: options.honeypot || '',
    timing: {
      clientStartedAt,
      clientSubmittedAt: Date.now(),
      inputDurationMs: options.inputDurationMs,
      focusDurationMs: options.focusDurationMs,
    },
    pow,
  };
}

export type AccountSecurityOptions = {
  honeypot?: string;
  inputDurationMs?: number;
  focusDurationMs?: number;
  signal?: AbortSignal;
};

export async function withAccountSecurityRetry<T>(
  api: PublicApiClient,
  purpose: AccountChallengePurpose,
  clientStartedAt: number,
  options: AccountSecurityOptions,
  submit: (security: AccountSecurityPayload) => Promise<T>,
): Promise<T> {
  const firstSecurity = await prepareAccountSecurity(api, purpose, clientStartedAt, options);
  try {
    return await submit(firstSecurity);
  } catch (error) {
    if (!(error instanceof PublicApiError) || error.code !== 'ACCOUNT_CHALLENGE_CLIENT_MISMATCH') {
      throw error;
    }
    const retrySecurity = await prepareAccountSecurity(api, purpose, clientStartedAt, options);
    return submit(retrySecurity);
  }
}

export function accountGet<T>(api: PublicApiClient, path: string): Promise<T> {
  return api.request<T>(path, { cache: 'no-store' });
}

export function accountPost<T>(api: PublicApiClient, path: string, body: unknown): Promise<T> {
  return api.request<T>(path, { method: 'POST', body });
}

export function accountPut<T>(api: PublicApiClient, path: string, body: unknown): Promise<T> {
  return api.request<T>(path, { method: 'PUT', body });
}

export function accountDelete<T>(api: PublicApiClient, path: string, body?: unknown): Promise<T> {
  return api.request<T>(path, { method: 'DELETE', body });
}
